import { PanelRightClose, PanelRightOpen } from "lucide-react";
import EMBLEMA from "@/assets/EMBLEMA-RETRO-IA.png";

function AvatarCoach({ size = 32 }: { size?: number }) {
  return (
    <span
      className="flex flex-shrink-0 items-center justify-center rounded-full bg-[var(--primary)]"
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <img src={EMBLEMA} alt="" className="h-3/5 w-3/5 object-contain brightness-0 invert" />
    </span>
  );
}

type Props = {
  children: React.ReactNode;
  /** Si se pasa, muestra el botón para contraer el panel (solo escritorio). */
  onContraer?: () => void;
};

/** Contenedor del coach virtual: encabezado y chat. */
export function CoachPanel({ children, onContraer }: Props) {
  return (
    <section
      aria-label="Coach virtual"
      className="flex h-full min-h-0 flex-col bg-gradient-to-b from-[var(--linear-gradient-start)] to-[var(--linear-gradient-end)]"
    >
      <header className="flex flex-shrink-0 items-center gap-3 border-b border-[var(--border)] bg-[var(--card-background)] px-4 py-3">
        <AvatarCoach />
        <div className="flex min-w-0 flex-1 flex-col leading-tight">
          <span className="text-sm font-semibold text-[var(--foreground)]">Coach virtual</span>
          <span className="truncate text-xs text-[var(--foreground)] opacity-60">
            Pregúntale sobre tu retroalimentación
          </span>
        </div>
        {onContraer && (
          <button
            type="button"
            onClick={onContraer}
            aria-label="Contraer coach"
            title="Contraer coach"
            className="flex h-8 w-8 items-center justify-center rounded-md border border-[var(--border)] text-[var(--foreground)] opacity-70 transition hover:opacity-100 focus-visible:outline-2 focus-visible:outline-[var(--primary)]"
          >
            <PanelRightClose size={16} />
          </button>
        )}
      </header>
      <div className="flex min-h-0 flex-1 flex-col">{children}</div>
    </section>
  );
}

/** Barra lateral angosta que se muestra con el coach contraído. */
export function CoachRiel({
  onExpandir,
  hayRespuestaNueva,
}: {
  onExpandir: () => void;
  hayRespuestaNueva: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onExpandir}
      aria-label="Abrir coach virtual"
      title="Abrir coach virtual"
      className="flex w-14 flex-shrink-0 flex-col items-center gap-3 border-l border-[var(--border)] bg-[var(--card-background)] py-3 text-[var(--foreground)] transition hover:bg-[var(--primary)]/5 focus-visible:outline-2 focus-visible:outline-[var(--primary)]"
    >
      <PanelRightOpen size={16} className="opacity-70" />
      <span className="relative">
        <AvatarCoach />
        {hayRespuestaNueva && (
          <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-[var(--card-background)] bg-[var(--primary)]" />
        )}
      </span>
      <span className="text-xs font-semibold uppercase tracking-wide opacity-60 [writing-mode:vertical-rl] rotate-180">
        Coach virtual
      </span>
    </button>
  );
}

/** Preguntas sugeridas como chips desplazables, encima de la caja de texto. */
export function PreguntasSugeridas({
  preguntas,
  usadas,
  onEnviar,
  disabled,
}: {
  preguntas: string[];
  usadas: Set<string>;
  onEnviar: (pregunta: string) => void;
  disabled?: boolean;
}) {
  if (preguntas.length === 0) return null;
  return (
    <div className="flex-shrink-0 px-4 pt-2">
      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-[var(--foreground)] opacity-50">
        Preguntas sugeridas
      </p>
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-primary">
        {preguntas.map((pregunta) => {
          const usada = usadas.has(pregunta);
          return (
            <button
              key={pregunta}
              type="button"
              onClick={() => onEnviar(pregunta)}
              disabled={usada || disabled}
              title={usada ? "Ya hiciste esta pregunta" : pregunta}
              className={`max-w-[18rem] flex-shrink-0 truncate rounded-full border px-3 py-1.5 text-xs transition ${
                usada
                  ? "border-[var(--border)] text-[var(--foreground)] opacity-50 line-through"
                  : "border-[var(--primary)] bg-[var(--card-background)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-white"
              } disabled:cursor-not-allowed`}
            >
              {pregunta}
            </button>
          );
        })}
      </div>
    </div>
  );
}
